import {
  ADMIN_ROUTE_COMMANDS,
  ADMIN_ROUTE_PATHS,
} from 'core/constants/admin-route-paths';
import {
  PlatformSearchEntityType,
  PlatformSearchItem,
} from 'core/models/platform-search-result.model';
import { Role } from 'core/models/role.model';

function joinCommands(commands: readonly string[]): string {
  if (commands.length === 0) {
    return ADMIN_ROUTE_PATHS.dashboard;
  }
  if (commands.length === 1) {
    return commands[0] ?? ADMIN_ROUTE_PATHS.dashboard;
  }
  return `/${commands
    .map((part) => part.replace(/^\/+|\/+$/g, ''))
    .filter(Boolean)
    .join('/')}`;
}

function routeForUserRole(id: string, role: string | undefined): string {
  switch (role as Role | undefined) {
    case 'STUDENT':
    case 'TEACHER':
    case 'GUARDIAN':
    case 'SECRETARY':
    case 'FINANCE':
    case 'TRANSPORT_OPERATOR':
    case 'INSTITUTION_ADMIN':
    case 'SUPPORT':
    case 'PLATFORM_ADMIN':
    case 'SUPER_ADMIN':
      return joinCommands(ADMIN_ROUTE_COMMANDS.userDetails(id));
    default:
      return joinCommands(ADMIN_ROUTE_COMMANDS.userDetails(id));
  }
}

function routeFromHint(
  id: string,
  hint: string | undefined,
  role: string | undefined,
): string | undefined {
  switch (hint) {
    case 'students':
    case 'teachers':
    case 'guardians':
    case 'support-staff':
    case 'users':
      return routeForUserRole(id, role);
    case 'classes':
      return joinCommands(ADMIN_ROUTE_COMMANDS.classDetails(id));
    case 'enrollments':
      return joinCommands(ADMIN_ROUTE_COMMANDS.enrollmentDetails(id));
    case 'institutions':
      return joinCommands(ADMIN_ROUTE_COMMANDS.institutionDetails(id));
    case 'academic_courses':
    case 'academic-courses':
      return joinCommands(ADMIN_ROUTE_COMMANDS.academicCourseDetails(id));
    case 'subjects':
      return joinCommands(ADMIN_ROUTE_COMMANDS.subjectDetails(id));
    default:
      return undefined;
  }
}

function routeFromType(item: PlatformSearchItem): string | undefined {
  const role = item.meta?.role;

  switch (item.type) {
    case 'user':
      return routeForUserRole(item.id, role);
    case 'class':
      return joinCommands(ADMIN_ROUTE_COMMANDS.classDetails(item.id));
    case 'enrollment':
      return joinCommands(ADMIN_ROUTE_COMMANDS.enrollmentDetails(item.id));
    case 'institution':
      return joinCommands(ADMIN_ROUTE_COMMANDS.institutionDetails(item.id));
    case 'academic_course':
      return joinCommands(ADMIN_ROUTE_COMMANDS.academicCourseDetails(item.id));
    case 'subject':
      return joinCommands(ADMIN_ROUTE_COMMANDS.subjectDetails(item.id));
    case 'page':
      return item.route ?? item.id;
    default:
      return undefined;
  }
}

/** Resolve rota de navegacao a partir do item da busca global. */
export function resolvePlatformSearchRoute(
  item: PlatformSearchItem,
): string | undefined {
  if (item.route?.trim()) {
    return item.route.trim();
  }

  return (
    routeFromHint(item.id, item.routeHint, item.meta?.role) ??
    routeFromType(item)
  );
}

export function defaultIconForSearchType(
  type: PlatformSearchEntityType,
): string {
  switch (type) {
    case 'user':
      return 'person';
    case 'class':
      return 'people';
    case 'enrollment':
      return 'journal-check';
    case 'institution':
      return 'building';
    case 'academic_course':
      return 'mortarboard';
    case 'subject':
      return 'book';
    case 'page':
      return 'file-earmark';
    default:
      return 'search';
  }
}

export function labelForSearchType(type: PlatformSearchEntityType): string {
  switch (type) {
    case 'user':
      return 'Usuários';
    case 'class':
      return 'Turmas';
    case 'enrollment':
      return 'Matrículas';
    case 'institution':
      return 'Instituições';
    case 'academic_course':
      return 'Cursos acadêmicos';
    case 'subject':
      return 'Disciplinas';
    case 'page':
      return 'Páginas';
    default:
      return 'Resultados';
  }
}
